import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { MdLocalFireDepartment, MdEmojiEvents, MdTrendingUp, MdStars, MdLocalMovies } from "react-icons/md";
import RewardPopup from "../components/RewardPopup";

const API = process.env.REACT_APP_API_URL || '';

export default function ProgressPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reward, setReward] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const fetchUserProfile = async () => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    try {
      setLoading(true);
      // Add timestamp to prevent browser caching
      const res = await axios.get(`${API}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { _t: Date.now() }
      });
      setUser(res.data);
      
      // Check for new reward
      if (res.data.newReward) {
        setReward(res.data.newReward);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname]);

  if (loading) {
    return (
      <div className="progress-page">
        <div className="loading-indicator">Loading progress...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="progress-page">
        <div className="loading-indicator">Please login to view progress</div>
      </div>
    );
  }

  return (
    <div className="progress-page">
      <h2>Progress</h2>

      {user.levelInfo && (
        <div className="profile-section level-section">
          <h3 className="section-title">
            <MdTrendingUp />
            <span>Level {user.levelInfo.level}</span>
          </h3>
          <div className="level-card">
            <div className="level-progress-container">
              <div className="level-progress-bar">
                <div 
                  className="level-progress-fill" 
                  style={{ width: `${Math.min(user.levelInfo.progress, 100)}%` }}
                ></div>
              </div>
            </div>
            <div className="level-info">
              <div className="level-points">
                {user.levelInfo.currentPoints.toLocaleString()} / {user.levelInfo.maxPoints.toLocaleString()} points
              </div>
              <div className="level-next">
                {user.levelInfo.pointsNeeded.toLocaleString()} points to next level
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="profile-section">
        <h3 className="section-title">
          <MdLocalFireDepartment />
          <span>Streak Stats</span>
        </h3>
        <div className="stats-grid">
          <div className="stat-card streak-current">
            <div className="stat-icon">
              <MdLocalFireDepartment />
            </div>
            <div className="stat-content">
              <div className="stat-label">Current Streak</div>
              <div className="stat-value">{user.currentStreak || 0} days</div>
            </div>
          </div>
          <div className="stat-card streak-max">
            <div className="stat-icon">
              <MdEmojiEvents />
            </div>
            <div className="stat-content">
              <div className="stat-label">Best Streak</div>
              <div className="stat-value">{user.maxStreak || 0} days</div>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3 className="section-title">
          <MdTrendingUp />
          <span>Voting Stats</span>
        </h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Points Available Today</div>
              <div className="stat-value">{user.pointsLeftToday || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Total Votes Cast</div>
              <div className="stat-value">{user.totalVotes || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {user.levelInfo && user.levelInfo.nextRewards && user.levelInfo.nextRewards.length > 0 && (
        <div className="profile-section next-rewards-section">
          <h3 className="section-title next-rewards-title">
            <MdStars />
            <span>Next Available Rewards</span>
          </h3>
          <div className="rewards-list">
            {user.levelInfo.nextRewards.map((nextReward, index) => {
              const getIcon = () => {
                switch (nextReward.type) {
                  case 'points':
                    return <MdStars className="reward-icon" />;
                  case 'movie_coupon':
                    return <MdLocalMovies className="reward-icon" />;
                  default:
                    return <MdStars className="reward-icon" />;
                }
              };

              return (
                <div key={index} className="reward-item">
                  <div className="reward-level-badge">L{nextReward.level}</div>
                  <div className="reward-info">
                    <div className="reward-level-text">Level {nextReward.level}</div>
                    <div className="reward-description">{nextReward.description}</div>
                  </div>
                  {getIcon()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <RewardPopup reward={reward} onClose={() => setReward(null)} />
    </div>
  );
}

